import React, { useState, useMemo } from "react";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import DataTable from "react-data-table-component";
import { ErrorBoundary } from "storybook-dashboard/errors/handleError";

import { BSBtnDropdown, BSCard, BSBtn } from "traec-react/utils/bootstrap";
import { Modal, useModal } from "storybook-dashboard/components/modal";
import CustomDropdown from "./dropdown";
import Im from "immutable";
import { formEventToData } from "../utils";
import { BaseMetricForm } from "./basemetrics";
import { unitChoices } from "AppSrc/project/metrics/forms";
import DatePicker from "react-date-picker";
import Moment from "moment";
import { Hide } from "storybook-dashboard/utils";

// Column definitions
const getConversionFactorColumns = (groupId) => [
  {
    name: "ID",
    selector: (row) => row.get("uid").substring(0, 8),
    sortable: true,
    width: "100px",
  },
  {
    name: "From Metric ID",
    selector: (row) => row.getIn(["metric", "uid"])?.substring(0, 8) || "",
    sortable: true,
    width: "130px",
  },
  {
    name: "From Metric Name",
    selector: (row) => row.getIn(["metric", "name"]) || "",
    sortable: true,
    grow: 1,
  },
  {
    name: "From Unit",
    selector: (row) => row.getIn(["metric", "unit"]) || "",
    sortable: true,
    width: "100px",
  },
  {
    name: "To Unit",
    selector: (row) => row.get("toUnit") || "",
    sortable: true,
    width: "100px",
  },
  {
    name: "Factor",
    selector: (row) => row.get("factor") || "",
    sortable: true,
    width: "100px",
  },
  {
    name: "Actions",
    cell: (row) => <ActionsCell row={row} groupId={groupId} />,
    width: "100px",
    right: true,
  },
];

const editConversionFactor = ({ e, conversionFactorId, groupId, onSuccess }) => {
  let data = formEventToData(e);

  if (groupId) {
    data.group = groupId;
  }

  const queryParams = groupId ? `?groupId=${groupId}` : "";
  const url = `/api/tenant/admin/conversion/${conversionFactorId}/${queryParams}`;
  doFetch(url, "PATCH", data).then((response) => {
    if (response.ok) {
      onSuccess?.();
    }
  });
};

const useTriggerAll = (groupId) => {
  const queryParams = groupId ? `?groupId=${groupId}` : "";
  const { trigger: triggerAll } = useApi(`/api/tenant/admin/conversion/`);
  const { trigger: triggerGroup } = useApi(`/api/tenant/admin/conversion/${queryParams}`);
  return {
    trigger: () => {
      triggerAll();
      if (groupId) triggerGroup();
    },
  };
};

function EditConversionFactorModal({ conversionFactor, groupId }) {
  const { trigger } = useTriggerAll(groupId);
  const { hideModal } = useModal();

  let conversionFactorId = conversionFactor.get("uid");
  let metricId = conversionFactor.getIn(["metric", "uid"]) || "";
  let toUnit = conversionFactor.getIn(["toUnit"]);

  const onSuccess = () => {
    trigger();
    hideModal();
  };

  return (
    <Modal title={`Edit conversion factor from metric [${metricId.substring(0, 8)}] to ${toUnit}`}>
      <form onSubmit={(e) => editConversionFactor({ e, conversionFactorId, groupId, onSuccess })}>
        <div className="form-group">
          <label>Factor</label>
          <input type="text" className="form-control" name="factor" defaultValue={conversionFactor.get("factor")} />
        </div>
        <button type="submit" className="btn btn-sm btn-primary">
          Save
        </button>
      </form>
    </Modal>
  );
}

const deleteConversionFactor = ({ conversionFactorId, groupId, onSuccess }) => {
  const queryParams = groupId ? `?groupId=${groupId}` : "";
  doFetch(`/api/tenant/admin/conversion/${conversionFactorId}/${queryParams}`, "DELETE", {}) //
    .then((response) => {
      if (response.ok) {
        console.log("Deleted conversion factor", response);
        onSuccess?.();
      }
    });
};

const syncWithLinked = ({ conversionFactor }) => {
  let metricId = conversionFactor.getIn(["metric", "uid"]);

  doFetch("/api/tenant/admin/dispatch/", "POST", {
    action: "SYNC_CONVERSION_FACTORS",
    payload: {
      metric_id: metricId,
    },
  });
};

// Actions cell component
function ActionsCell({ row, groupId }) {
  const { trigger } = useTriggerAll(groupId);
  const { setModal, hideModal } = useModal();

  const conversionFactorId = row.get("uid");

  const onSuccess = () => {
    trigger();
    hideModal();
  };

  const dropDownItems = [
    {
      name: "Edit in all Groups and Reports",
      onClick: () => {
        setModal(<EditConversionFactorModal conversionFactor={row} />);
      },
    },
    // {
    //   name: "Copy to Linked Metrics",
    //   onClick: () => {
    //     syncWithLinked({ conversionFactor: row, groupId });
    //   },
    // },
  ];

  if (groupId) {
    dropDownItems.push(
      {}, // Divider
      {
        name: "Edit in this Group only",
        onClick: () => {
          setModal(<EditConversionFactorModal conversionFactor={row} groupId={groupId} />);
        },
      },
      {
        name: "Delete from only this Group",
        onClick: () => {
          deleteConversionFactor({ conversionFactorId, groupId, onSuccess: () => trigger() });
        },
      }
    );
  }

  dropDownItems.push(
    {}, // Divider
    {
      name: "Delete",
      onClick: () => {
        deleteConversionFactor({ conversionFactorId, onSuccess: () => trigger() });
      },
    }
  );

  return <CustomDropdown items={dropDownItems} />;
}

function ConversionFactorOptions({ groups }) {
  return (groups || Im.List())
    .toList()
    .map((item, i) => (
      <option key={i} value={item.get("id")}>
        {item.get("name")}
      </option>
    ))
    .unshift(<option key={-1} value=""></option>);
}

function ConversionFactorGroupSelect({ groups, currentGroupId, onChangeHandler }) {
  return (
    <div className="form-group">
      <label>Current Group</label>
      <select className="form-control" onChange={onChangeHandler} value={currentGroupId || ""}>
        <ConversionFactorOptions groups={groups} />
      </select>
    </div>
  );
}

const addOrUpdateConversionFactorGroup = ({ e, groupId, onSuccess }) => {
  const _data = formEventToData(e);

  const data = {
    name: _data.name,
    start_date: Moment(_data.start_date).format("YYYY-MM-DDTHH:mm:ss"),
  };

  const method = groupId ? "PATCH" : "POST";

  const url = `/api/tenant/admin/conversion_group/${groupId ? groupId + "/" : ""}`;
  doFetch(url, method, data).then(() => {
    onSuccess?.();
  });
};

function ConversionFactorGroupForm({ currentGroup: group, hideModal }) {
  let groupId = group?.get("id");
  const startDate = group?.get("start_date") || "";

  const { trigger } = useApi("/api/tenant/admin/conversion_group/");
  const [formStartDate, setFormStartDate] = useState(startDate);

  const onSuccess = () => {
    trigger();
    hideModal?.();
  };

  return (
    <form onSubmit={(e) => addOrUpdateConversionFactorGroup({ e, groupId, onSuccess })}>
      <div className="row">
        <div className="col-sm-4">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              defaultValue={group?.get("name") || ""}
              required
            />
          </div>
        </div>
        <div className="col-sm-4">
          <div className="form-group">
            <label htmlFor="start_date">Start Date</label>
            <DatePicker
              className="form-control datepicker-fullwidth"
              name="start_date"
              value={formStartDate ? new Date(formStartDate) : formStartDate}
              onChange={(v) => setFormStartDate(v)}
            />
          </div>
        </div>
        <div className="col-sm-4">
          <div className="form-group">
            <label>&nbsp;</label>
            <button type="submit" className="btn btn-primary form-control">
              Update
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

function ConversionFactorGroupFormModal(props) {
  const { hideModal } = useModal();
  const { group } = props;
  return (
    <Modal title={`${group ? "Edit" : "New"} Conversion Factor Group`}>
      <ConversionFactorGroupForm {...props} hideModal={hideModal} />
    </Modal>
  );
}

function ConversionFactorGroupCard(props) {
  const { currentGroupId } = props;
  const { setModal } = useModal();

  return (
    <BSCard
      widthOffset="col-sm-12 m-0 p-0"
      title="Conversion Factor Groups"
      button={<BSBtn onClick={() => setModal(<ConversionFactorGroupFormModal />)} text="New Group" />}
      body={
        <>
          <ConversionFactorGroupSelect {...props} />
          <Hide when={!currentGroupId}>
            <ConversionFactorGroupForm {...props} />
          </Hide>
        </>
      }
    />
  );
}

const addConversionFactor = ({ e, groupId, onSuccess }) => {
  let data = formEventToData(e);
  if (groupId) {
    data.group = groupId;
  }
  doFetch("/api/tenant/admin/conversion/", "POST", data).then(() => {
    onSuccess?.();
  });
};

function AddConversionFactorModal({ groupId }) {
  return (
    <Modal title={`Add a conversion factor to group ${groupId}`}>
      <AddMetricForm groupId={groupId} />
    </Modal>
  );
}

function AddMetricForm({ groupId }) {
  const { trigger } = useTriggerAll(groupId);
  const [showBaseMetricForm, setShowBaseMetricForm] = useState(false);
  const [metricId, setMetricId] = useState(null);
  const { setModal, hideModal } = useModal();

  const onSuccess = (response) => {
    response.json().then((json) => {
      setMetricId?.(json.uid);
      setShowBaseMetricForm(false);
    });
  };

  const onAddConversionFactorSuccess = () => {
    trigger();
    hideModal();
  };

  if (showBaseMetricForm) {
    return <BaseMetricForm onSuccess={onSuccess} />;
  }

  return (
    <>
      <div className="row">
        <div className="col-sm-12">
          <p className="text-primary" style={{ cursor: "pointer" }} onClick={() => setShowBaseMetricForm(true)}>
            Get or Create a Base Metric
          </p>
        </div>
      </div>
      <form onSubmit={(e) => addConversionFactor({ e, groupId, onSuccess: onAddConversionFactorSuccess })}>
        <div className="row">
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="metric">Metric</label>
              <input
                type="text"
                className="form-control"
                id="metric"
                name="metric"
                value={metricId ? metricId.substring(0, 8) : ""}
                readOnly
                required
              />
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="toUnit">To Unit</label>
              <select className="form-control" id="toUnit" name="toUnit" required>
                <option value="">Select a unit...</option>
                {unitChoices.map((unit, index) => (
                  <option key={index} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-sm-4">
            <div className="form-group">
              <label htmlFor="factor">Factor</label>
              <input type="number" step="any" className="form-control" id="factor" name="factor" required />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <button type="submit" className="btn btn-primary">
              Add Conversion Factor
            </button>
          </div>
        </div>
      </form>
    </>
  );
}

function AdminMenu({ groupId }) {
  const { setModal } = useModal();

  if (!groupId) return null;

  return (
    <>
      <div className="btn btn-sm btn-primary mb-3 mt-2">
        <BSBtnDropdown
          header={"Admin"}
          links={[
            {
              name: "Add a Metric",
              onClick: (e) => {
                setModal(<AddConversionFactorModal groupId={groupId} />);
              },
            },
          ]}
        />
      </div>
    </>
  );
}

export default function ConversionFactorAdmin() {
  const [groupId, setGroupId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { data: conversionFactorGroups } = useApi("/api/tenant/admin/conversion_group/");
  const queryParams = groupId ? `?groupId=${groupId}` : "";
  const { data: conversionFactors } = useApi(`/api/tenant/admin/conversion/${queryParams}`);
  const { data: basemetrics } = useApi("/api/tenant/admin/basemetric/");

  const columns = useMemo(() => getConversionFactorColumns(groupId), [groupId]);

  // Get the set of conversion factors to render (if groupId==null then render all)
  let _conversionFactors = conversionFactors;
  if (groupId) {
    _conversionFactors = conversionFactorGroups.getIn([groupId, "conversionFactors", "byId"]) || Im.Map();
    _conversionFactors = _conversionFactors.toList ? _conversionFactors.toList() : _conversionFactors;
  }

  // Sort conversion factors by metric name
  const sortedConversionFactors = useMemo(() => {
    return (conversionFactors || Im.List()).sortBy((i) => {
      const name = i.getIn(["metric", "name"]);
      return name ? name.trim() : "";
    });
  }, [conversionFactors]);

  // Filter conversion factors based on search term
  const filteredConversionFactors = useMemo(() => {
    if (!searchTerm.trim()) {
      return sortedConversionFactors;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return sortedConversionFactors.filter((convFact) => {
      const id = convFact.get("uid") || "";
      const metricName = convFact.getIn(["metric", "name"]) || "";
      const metricId = convFact.getIn(["metric", "uid"]) || "";
      const fromUnit = convFact.getIn(["metric", "unit"]) || "";
      const toUnit = convFact.get("toUnit") || "";

      return (
        id.toLowerCase().includes(lowerSearchTerm) ||
        metricName.toLowerCase().includes(lowerSearchTerm) ||
        metricId.toLowerCase().includes(lowerSearchTerm) ||
        fromUnit.toLowerCase().includes(lowerSearchTerm) ||
        toUnit.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [sortedConversionFactors, searchTerm]);

  const currentGroup = useMemo(() => {
    return conversionFactorGroups?.find((i) => String(i.get("id")) === String(groupId)) || null;
  }, [conversionFactorGroups, groupId]);

  console.log("Rendering ConversionFactorAdmin", groupId, currentGroup?.toJS());

  return (
    <div className="container-fluid">
      <h3>Conversion Factors in Tenancy</h3>
      <p>This gives a list of all Conversion Factors within this tenancy</p>

      <div className="row">
        <ConversionFactorGroupCard
          groups={conversionFactorGroups}
          currentGroupId={groupId}
          currentGroup={currentGroup}
          baseMetrics={basemetrics}
          onChangeHandler={(e) => {
            setGroupId(e.target.value);
          }}
        />
      </div>

      <div className="row mb-3 align-items-end">
        <div className="col-sm-6">
          <input
            type="text"
            className="form-control"
            placeholder="Search by ID, Metric Name, or Units..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-sm-6 text-right">
          <AdminMenu groupId={groupId} />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredConversionFactors.toArray()}
        pagination
        paginationPerPage={20}
        paginationRowsPerPageOptions={[10, 20, 50, 100]}
        highlightOnHover={true}
        striped={true}
        defaultSortFieldId={3} // Sort by Metric Name column by default
      />
    </div>
  );
}
